import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { MasterEventProvider, useMasterEvent } from '../../context/MasterEventContext';
import { notificationsService } from '../../services/notifications';
import { roleLabel } from '../../services/roles';
import ChatSidebar from '../shared/ChatSidebar';
import Avatar from '../shared/Avatar';
import TeamTag from '../shared/TeamTag';
import './MasterEventLayout.css';

function NotificationBell({ userId }) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const refresh = () => {
      setNotifications(notificationsService.getForUser(userId, 15));
      setUnreadCount(notificationsService.getUnreadCount(userId));
    };
    refresh();
    const i = setInterval(refresh, 5000);
    return () => clearInterval(i);
  }, [userId]);

  useEffect(() => {
    const handleClick = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const refresh = () => {
    setNotifications(notificationsService.getForUser(userId, 15));
    setUnreadCount(notificationsService.getUnreadCount(userId));
  };
  const handleOpen = () => { setOpen(!open); };
  const handleMarkAllRead = () => { notificationsService.markAllRead(userId); refresh(); };
  const handleClickNotif = (n) => { notificationsService.markRead(n.id); refresh(); setOpen(false); };

  const timeAgo = (d) => {
    const mins = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  return (
    <div className="mel-notif" ref={dropdownRef}>
      <button className="mel-notif-btn" onClick={handleOpen} title="Notifications">
        🔔
        {unreadCount > 0 && <span className="mel-notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
      </button>
      {open && (
        <div className="mel-notif-dropdown">
          <div className="mel-notif-header">
            <span className="mel-notif-title">Notifications</span>
            {unreadCount > 0 && (
              <button className="mel-notif-markall" onClick={handleMarkAllRead}>Mark all read</button>
            )}
          </div>
          <div className="mel-notif-list">
            {notifications.length === 0 ? (
              <div className="mel-notif-empty">No notifications</div>
            ) : (
              notifications.map((n) => (
                <div key={n.id} className={`mel-notif-item ${!n.read ? 'mel-notif-item--unread' : ''}`} onClick={() => handleClickNotif(n)}>
                  <div className="mel-notif-item-icon">
                    {n.type === 'mention' ? '💬' : n.type === 'activity' ? '📌' : '🔔'}
                  </div>
                  <div className="mel-notif-item-content">
                    <div className="mel-notif-item-title">{n.title}</div>
                    {n.body && <div className="mel-notif-item-body">{n.body}</div>}
                  </div>
                  <span className="mel-notif-item-time">{timeAgo(n.createdAt)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function AvatarDropdown({ user, onSignOut, onProfile, isOwner, members }) {
  const [open, setOpen] = useState(false);
  const [showImpersonate, setShowImpersonate] = useState(false);
  const { impersonate, isImpersonating, stopImpersonating } = useAuth();
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Memberships may be keyed by Cognito sub or local id; getMembers resolves m.user.
  const isMe = (m) => m.user?.id === user?.id || (user?.cognitoId && m.userId === user.cognitoId);
  const impersonable = (members || []).filter((m) => m.user && !m.userId?.startsWith('invite:') && !isMe(m));

  const handleImpersonate = (target) => {
    impersonate(target);
    setOpen(false);
    setShowImpersonate(false);
  };

  return (
    <div className="mel-avatar-dropdown" ref={ref}>
      {isImpersonating && (
        <button onClick={() => stopImpersonating()} className="mel-impersonate-banner" title="Preview only: you see what this person sees, but anything you do is still saved as you. Click to stop.">
          🎭 Viewing as {user?.displayName}
        </button>
      )}
      <button className={`mel-nav-avatar ${isImpersonating ? 'mel-nav-avatar--impersonating' : ''}`} onClick={() => setOpen(!open)} title={user?.displayName}>
        <Avatar user={user} size={28} />
      </button>
      {open && (
        <div className="mel-avatar-menu">
          <div className="mel-avatar-menu-header">
            <Avatar user={user} size={20} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{user?.displayName}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{user?.email}</div>
            </div>
          </div>
          <button className="mel-avatar-menu-item" onClick={() => { setOpen(false); onProfile(); }}>
            <span>👤</span> Profile
          </button>
          {isOwner && !isImpersonating && (
            <button className="mel-avatar-menu-item" onClick={() => setShowImpersonate(!showImpersonate)}>
              <span>🎭</span> Impersonate {showImpersonate ? '▼' : '▶'}
            </button>
          )}
          {showImpersonate && (
            <div style={{ maxHeight: 200, overflowY: 'auto', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ padding: '6px 14px', fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
                Preview the event as another member. Actions are still saved as you.
              </div>
              {impersonable.length === 0 && <div style={{ padding: '6px 14px', fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>No other members in this event yet.</div>}
              {impersonable.map((m) => (
                <button key={m.userId} className="mel-avatar-menu-item" onClick={() => handleImpersonate(m.user)}>
                  <Avatar user={m.user} size={16} /> {m.user.displayName || m.user.email}
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{roleLabel(m.role)}</span>
                </button>
              ))}
            </div>
          )}
          {isImpersonating && (
            <button className="mel-avatar-menu-item" style={{ color: '#f59e0b' }} onClick={() => { stopImpersonating(); setOpen(false); }}>
              <span>🔙</span> Stop impersonating
            </button>
          )}
          <button className="mel-avatar-menu-item mel-avatar-menu-item--danger" onClick={() => { setOpen(false); onSignOut(); }}>
            <span>↪</span> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function MasterEventLayoutInner() {
  const { user, signOut } = useAuth();
  const { masterEvent, isAdmin, isOwner, members, loading, error, refreshData } = useMasterEvent();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isActivityPage = location.pathname.includes('/activity/');

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="mel-layout">
        <div className="mel-loading">Loading event...</div>
      </div>
    );
  }

  if (error) return <div role="alert" className="mea-error">{error}<button onClick={refreshData}>Retry</button></div>;

  if (!masterEvent) {
    return (
      <div className="mel-layout">
        <div className="mel-not-found">
          <h2>Event not found</h2>
          <button className="mel-back-btn" onClick={() => navigate('/')}>Back to Events</button>
        </div>
      </div>
    );
  }

  const basePath = `/me/${id}`;

  return (
    <div className={`mel-layout ${isActivityPage ? 'mel-layout--activity' : 'mel-layout--event'}`}>
      <nav className={`mel-nav ${isActivityPage ? 'mel-nav--activity' : ''}`}>
        <div className="mel-nav-inner">
          <button className="mel-nav-back" onClick={() => navigate('/')} title="Back to events">
            ←
          </button>
          <div className="mel-nav-event-name">{masterEvent.name}</div>

          <button className="mel-nav-mobile-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? '✕' : '☰'}
          </button>

          <div className={`mel-nav-links ${mobileMenuOpen ? 'mel-nav-links--open' : ''}`}>
            <NavLink to={basePath} end className="mel-nav-link" onClick={() => setMobileMenuOpen(false)}>
              <span className="mel-nav-link-icon">🏠</span> Overview
            </NavLink>
            <NavLink to={`${basePath}/feed`} className="mel-nav-link" onClick={() => setMobileMenuOpen(false)}>
              <span className="mel-nav-link-icon">💬</span> Feed
            </NavLink>
            <NavLink to={`${basePath}/scores`} className="mel-nav-link" onClick={() => setMobileMenuOpen(false)}>
              <span className="mel-nav-link-icon">🏆</span> Scores
            </NavLink>
            {isAdmin && (
              <NavLink to={`${basePath}/admin`} className="mel-nav-link" onClick={() => setMobileMenuOpen(false)}>
                <span className="mel-nav-link-icon">⚙️</span> Admin
              </NavLink>
            )}
          </div>

          <div className="mel-nav-user">
            {user && <NotificationBell userId={user.id} />}
            <TeamTag user={user} />
            <AvatarDropdown user={user} onSignOut={handleSignOut} onProfile={() => navigate('/profile')} isOwner={isOwner} members={members} />
          </div>
        </div>
      </nav>

      <main className="mel-main">
        <Outlet />
      </main>

      {user && masterEvent && <ChatSidebar masterEventId={id} user={user} feed={null} />}
    </div>
  );
}

export default function MasterEventLayout() {
  const { id } = useParams();

  return (
    <MasterEventProvider masterEventId={id}>
      <MasterEventLayoutInner />
    </MasterEventProvider>
  );
}
